import React, { useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router';

const tabs = [
  { name: 'Account', path: 'account' },
  { name: 'Plans & Billing', path: 'plan' },
  { name: 'User API Keys', path: 'api-keys' },
  { name: 'Widgets', path: 'widgets' },
  { name: 'Notifications', path: 'notifications' },
  { name: 'Appearance', path: 'appearance' },
];

/**
 * Layout for settings pages with tab navigation.
 */
const SettingsLayout: React.FC = () => {
  const location = useLocation();
  const currentTab = tabs.find(tab => location.pathname.includes(tab.path))?.path || 'account';
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const nav = navRef.current;
    const activeTab = nav?.querySelector<HTMLAnchorElement>('[data-active="true"]');
    if (!nav || !activeTab) return;

    let disposed = false;
    const ensureActiveTabIsVisible = () => {
      if (disposed) return;
      const navBox = nav.getBoundingClientRect();
      const tabBox = activeTab.getBoundingClientRect();
      if (tabBox.left >= navBox.left && tabBox.right <= navBox.right) return;

      const targetLeft = nav.scrollLeft
        + tabBox.left
        - navBox.left
        - (nav.clientWidth - tabBox.width) / 2;
      nav.scrollLeft = Math.max(0, targetLeft);
    };

    ensureActiveTabIsVisible();
    const frame = window.requestAnimationFrame(ensureActiveTabIsVisible);
    const settledLayoutTimer = window.setTimeout(ensureActiveTabIsVisible, 250);
    const resizeObserver = new ResizeObserver(ensureActiveTabIsVisible);
    resizeObserver.observe(nav);
    resizeObserver.observe(activeTab);
    document.fonts?.ready.then(ensureActiveTabIsVisible);

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settledLayoutTimer);
      resizeObserver.disconnect();
    };
  }, [currentTab]);

  return (
    <div className='h-full w-full overflow-y-auto px-6 pb-10 pt-4'>
      <nav ref={navRef} aria-label='Settings' className='mb-8 overflow-x-auto pb-2'>
        <div className='flex min-w-max items-center gap-2 rounded-2xl border border-secondary bg-surface-secondary p-1'>
          {tabs.map(tab => (
            <NavLink
              key={tab.path}
              data-active={currentTab === tab.path ? 'true' : undefined}
              to={`/settings/${tab.path}`}
              className={({ isActive }) =>
                `rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  isActive || currentTab === tab.path
                    ? 'bg-surface text-content shadow-sm'
                    : 'text-content-secondary hover:bg-hover hover:text-content'
                }`
              }
            >
              {tab.name}
            </NavLink>
          ))}
        </div>
      </nav>
      <div className='mx-auto max-w-4xl pb-10'>
        <Outlet />
      </div>
    </div>
  );
};

export default SettingsLayout;
